import { useCallback, useMemo, useState } from "react";
import type { View, ViewTemplate } from "@gooner/shared";
import { builtinViewId, viewFromTemplate } from "@gooner/shared";
import { api } from "../../lib/api";
import { ViewPreview } from "./ViewPreview";
import { CardShell, FooterAction } from "./CardShell";
import { IconCopy } from "../ui/icons";
import { useNavigate } from "react-router-dom";
import { viewGrid } from "../lib/format";
import { resolveFeed } from "../lib/monitorTemplates";
import { useSession } from "../../lib/session";
import { AuthModal } from "./AuthModal";
import type { CollectionOption } from "../lib/monitorTemplates";

/**
 * One built-in setup, under the same grammar as a view card: **clicking plays it, and its
 * one secondary action sits in the footer.** Both of them skip the editor, because a quick
 * setup is something to use, not a form to fill in.
 *
 * Neither half works alone. Click without Copy lets you watch a setup but never keep or
 * change one; Copy without click makes trying one a commitment. Together the gallery is
 * both "put this on a screen right now" and "start from this".
 *
 * **Playing saves nothing, and mints nothing.** A built-in is addressed directly, by a
 * permanent `builtin:` id that everybody shares, so putting one on a screen is a device
 * pointed at it and nothing else. Closing the tab removes the device; the setup was never
 * anyone's to expire. There is deliberately no save-on-close prompt: a prompt every time
 * would tax the common case, a glance, to rescue the rare one. If you wanted to keep it,
 * you would have hit Copy.
 *
 * **Copy does not open the editor**, and there is no Edit here at all. A built-in is never
 * editable: they are the same for everybody, and the gallery stops being a
 * gallery the moment one can be changed. What Copy hands back is an ordinary view, and
 * that is the thing with an Edit on it.
 *
 * The media is the same rendered preview a view card shows, addressed by the built-in's
 * own id — one component and one route for both, so a setup and a saved view cannot drift
 * into looking like different kinds of thing.
 */
export function QuickSetupCard({
  template,
  collections,
  onCopied,
  onError,
}: {
  template: ViewTemplate;
  collections: CollectionOption[];
  onCopied: (view: View) => void;
  onError: (message: string) => void;
}) {
  const user = useSession((s) => s.user);
  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // The id and timestamps viewFromTemplate mints are thrown away — the server assigns its
  // own — but the regions and aspect are what both actions send, so a copy and a play get
  // identical content.
  const draft = useMemo(
    () => viewFromTemplate(template, (feed) => resolveFeed(feed, collections), Date.now()),
    [template, collections],
  );
  const input = {
    name: draft.name,
    icon: draft.icon,
    aspect: draft.aspect,
    regions: draft.regions,
  };

  /**
   * Play it: open a CLAIMABLE screen on this setup, IN THIS TAB. Nothing is minted.
   *
   * `/screen` mints nothing, shows a code, and hands the screen to whoever reads it, so
   * sending the setup id along is the whole job (this used to be `api.devices.open(...)`
   * onto `/view/d/:id`, a device only the clicking session could drive).
   *
   * Same tab, not `_blank` (Casey 2026-10-09 #2): a new tab left the guest on a `/screen`
   * with no way back to the gallery but the Menu. The history state tells PendingScreen
   * to wear a "Back to views" beside the mark; browser Back works too. Nothing to await,
   * so no popup-blocker dance and no blocked-URL fallback is needed any more.
   */
  const navigate = useNavigate();
  const play = useCallback(() => {
    navigate(`/screen?setup=${encodeURIComponent(template.id)}`, { state: { from: "views" } });
  }, [navigate, template.id]);

  /**
   * Copy it into Your views and stay put.
   *
   * A copy is a SAVED view, which requires an account — asked for before the request
   * rather than after the 401, so the answer arrives with the click. Playing the setup
   * needs no account at all: that is a device pointed at a built-in and nothing is kept.
   */
  const copy = useCallback(() => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setBusy(true);
    void api.views
      .save(input)
      .then(({ view }) => onCopied(view))
      .catch((e) => onError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  }, [user, input, onCopied, onError]);

  const n = draft.regions.length;
  const grid = viewGrid(draft.regions);

  return (
    <CardShell
      busy={busy}
      onPlay={play}
      playLabel={`Play ${template.name} without saving it`}
      media={<ViewPreview viewId={builtinViewId(template.id)} name={template.name} />}
      badge={`${grid ? `${grid} · ` : ""}${n} ${n === 1 ? "region" : "regions"}`}
      name={template.name}
      icon={template.emoji}
      /*
        Copy sits in the footer, in the same icon-only style as a view card's Send to and
        Delete, rather than in the hover slot. The secondary actions on this screen now
        read as one row of controls in one place, whichever kind of card they are on.

        The hover slot is left empty rather than filled for symmetry: there is no Edit
        here and never will be. A built-in is not editable — they are the
        same for everybody, and the gallery stops being a gallery the moment one can be
        changed. What Copy hands back is an ordinary view, which is the thing with an
        Edit on it.
      */
      footerActions={
        <>
          <FooterAction
            label={
              user
                ? `Copy ${template.name} into your views`
                : `Sign in to copy ${template.name} into your views`
            }
            onClick={copy}
          >
            <IconCopy width={13} height={13} />
          </FooterAction>
          {authOpen ? (
            <AuthModal
              initialMode="register"
              reason="Copying a quick setup saves it to your account, so it follows you to every screen you sign in on."
              onClose={() => setAuthOpen(false)}
            />
          ) : null}
        </>
      }
      blockedUrl={null}
    />
  );
}
